import React from 'react';
import { Icon } from '@iconify/react';

export type KanbanCardDensity = 'detail' | 'compact';

export interface KanbanCardCell {
  key: string;
  content: React.ReactNode;
  className?: string;
  title?: string;
}

export interface KanbanCardCompactSchema {
  eyebrow?: React.ReactNode;
  title: React.ReactNode;
  meta?: React.ReactNode;
  aside?: React.ReactNode;
}

export interface KanbanCardSchema {
  columns?: string;
  left: KanbanCardCell[];
  right?: KanbanCardCell[];
  footer?: React.ReactNode;
  compact: KanbanCardCompactSchema;
}

interface SchemaKanbanCardProps {
  schema: KanbanCardSchema;
  density: KanbanCardDensity;
  selected?: boolean;
  title?: string;
  onClick: () => void;
  className?: string;
}

export const SchemaKanbanCard: React.FC<SchemaKanbanCardProps> = ({
  schema,
  density,
  selected = false,
  title,
  onClick,
  className = '',
}) => {
  const stateClass = selected
    ? 'bg-cyan-400/10 ring-1 ring-cyan-300/25'
    : 'bg-white/[0.02] hover:bg-white/[0.05]';

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      title={title}
      className={`w-full max-w-full overflow-hidden rounded-lg text-left transition-[background-color,box-shadow,transform] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-cyan-300/45 ${stateClass} ${density === 'compact' ? 'px-2 py-1.5' : 'px-2.5 py-2'} ${className}`}
    >
      {density === 'compact' ? (
        <div className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-2">
          <div className="min-w-0">
            {schema.compact.eyebrow && <div className="mb-1 flex min-w-0">{schema.compact.eyebrow}</div>}
            <div className="truncate text-[11px] font-black leading-4 text-white">{schema.compact.title}</div>
            {schema.compact.meta && <div className="mt-0.5 truncate text-[10px] font-semibold leading-4 text-white/42">{schema.compact.meta}</div>}
          </div>
          {schema.compact.aside && (
            <div className="flex min-w-[36px] justify-end text-[9px] font-black tabular-nums text-white/35">
              {schema.compact.aside}
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="grid min-w-0 gap-2" style={{ gridTemplateColumns: schema.columns || 'minmax(0,7fr) minmax(72px,3fr)' }}>
            <div className="min-w-0 space-y-1">
              {schema.left.map((cell) => (
                <div key={cell.key} title={cell.title} className={cell.className}>
                  {cell.content}
                </div>
              ))}
            </div>
            {schema.right && schema.right.length > 0 && (
              <div className="flex shrink-0 flex-col items-end justify-between gap-2">
                {schema.right.map((cell) => (
                  <div key={cell.key} title={cell.title} className={cell.className}>
                    {cell.content}
                  </div>
                ))}
              </div>
            )}
          </div>
          {schema.footer && <div className="mt-1.5">{schema.footer}</div>}
        </>
      )}
    </button>
  );
};

interface SchemaKanbanColumnProps {
  header: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  bodyClassName?: string;
  verticalScroll?: 'column' | 'page';
}

export const SchemaKanbanColumn: React.FC<SchemaKanbanColumnProps> = ({
  header,
  children,
  className = '',
  bodyClassName,
  verticalScroll = 'column',
}) => {
  const resolvedBodyClassName = bodyClassName
    || (verticalScroll === 'page'
      ? 'space-y-1'
      : 'min-h-0 flex-1 space-y-1 overflow-y-auto');

  return (
    <section className={`flex min-w-0 flex-col overflow-hidden rounded-xl border border-white/[0.045] p-2 shadow-[inset_0_1px_0_rgba(255,255,255,0.025)] ${className}`}>
      {header}
      <div className={resolvedBodyClassName}>{children}</div>
    </section>
  );
};

interface KanbanGroupHeaderProps {
  label: string;
  count: number;
  icon?: string;
  dotClassName?: string;
  toneClassName?: string;
  meta?: React.ReactNode;
  className?: string;
  countClassName?: string;
  countStyle?: React.CSSProperties;
  style?: React.CSSProperties;
  collapsed?: boolean;
  onToggle?: () => void;
  /** When true, render only a larger color dot + count for very dense surfaces. */
  dotOnly?: boolean;
}

export const KanbanGroupHeader: React.FC<KanbanGroupHeaderProps> = ({
  label,
  count,
  icon,
  dotClassName,
  toneClassName = 'text-white/72',
  meta,
  className = '',
  countClassName = '',
  countStyle,
  style,
  collapsed = false,
  onToggle,
  dotOnly = false,
}) => {
  const content = (
    <span className="flex min-w-0 flex-1 flex-col gap-1">
      <span className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-2">
        <span className="inline-flex min-w-0 items-center gap-1.5">
          {onToggle && <Icon icon={collapsed ? 'bx:chevron-right' : 'bx:chevron-down'} className="h-3.5 w-3.5 shrink-0 opacity-75" />}
          {dotClassName && (
            <span
              title={dotOnly ? label : undefined}
              className={`${dotOnly ? 'h-2.5 w-2.5' : 'h-2 w-2'} shrink-0 rounded-full ${dotClassName}`}
            />
          )}
          {!dotOnly && icon && <Icon icon={icon} className="h-3.5 w-3.5 shrink-0 opacity-80" />}
          {!dotOnly && <span className={`truncate text-[10px] font-black uppercase tracking-[0.1em] ${toneClassName}`}>{label}</span>}
        </span>
        <span
          className={`shrink-0 rounded-md border border-white/[0.055] bg-white/[0.065] px-1.5 py-0.5 text-[9px] font-black tabular-nums text-white/80 ${countClassName}`}
          style={countStyle}
        >
          {count}
        </span>
      </span>
      {meta && <span className="flex w-full min-w-0 flex-wrap items-center gap-1">{meta}</span>}
    </span>
  );

  // Default background stays dark; callers can pass a soft style when a group
  // needs stronger visual coding, such as campaign summaries in All view.
  const classes = `sticky top-0 z-10 mb-1 flex min-w-0 items-center justify-between gap-2 rounded-lg border border-white/[0.055] bg-black/30 px-2 py-1.5 shadow-[0_8px_18px_rgba(0,0,0,0.14)] backdrop-blur-md ${className}`;

  if (onToggle) {
    return (
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={!collapsed}
        className={`${classes} w-full text-left transition-colors hover:border-white/18 hover:bg-black/50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-cyan-300/35`}
        style={style}
      >
        {content}
      </button>
    );
  }

  return <div className={classes} style={style}>{content}</div>;
};
